// Écriture d'un OME-TIFF pyramidal au fil de l'eau : BigTIFF little-endian, tuiles JPEG RGB (YCbCr, comme les copies
// d'affichage du HES), pyramide en SubIFDs (convention OME / Bio-Formats, lue par QuPath, Fiji, Viv). Les tuiles sont
// ajoutées à la suite (une tuile uniforme déjà écrite est réutilisée), les IFD écrits à la fin, puis l'en-tête
// complété. sink : { write(position, Uint8Array) (promesse), close(), abort() }.
const TYPE = { ASCII: 2, SHORT: 3, LONG: 4, RATIONAL: 5, LONG8: 16, IFD8: 18 };
const SIZE = { [TYPE.ASCII]: 1, [TYPE.SHORT]: 2, [TYPE.LONG]: 4, [TYPE.RATIONAL]: 8, [TYPE.LONG8]: 8, [TYPE.IFD8]: 8 };

// Valeurs d'une entrée en octets little-endian.
function encode(type, values) {
  const n = type === TYPE.RATIONAL ? values.length / 2 : values.length;
  const bytes = new Uint8Array(n * SIZE[type]);
  const view = new DataView(bytes.buffer);
  values.forEach((v, i) => {
    if (type === TYPE.ASCII) bytes[i] = v;
    else if (type === TYPE.SHORT) view.setUint16(2 * i, v, true);
    else if (type === TYPE.LONG || type === TYPE.RATIONAL) view.setUint32(4 * i, v, true);
    else view.setBigUint64(8 * i, BigInt(v), true);
  });
  return bytes;
}

// Facteurs d'échantillonnage de la chrominance d'un JPEG (marqueur SOF : premier composant), [1, 1] par défaut.
export function jpegSubsampling(bytes) {
  let i = 2;
  while (i + 9 < bytes.length && bytes[i] === 0xff) {
    const marker = bytes[i + 1];
    const length = (bytes[i + 2] << 8) | bytes[i + 3];
    if (marker >= 0xc0 && marker <= 0xc2) {
      const sampling = bytes[i + 11];  // composant 1 : H << 4 | V
      return [sampling >> 4, sampling & 15];
    }
    i += 2 + length;
  }
  return [1, 1];
}

export class TiffPyramidWriter {
  constructor(sink) {
    this.sink = sink;
    this.pos = 16;  // en-tête BigTIFF, complété à la fin
    this.shared = new Map();  // clé d'une tuile uniforme -> { offset, length }
  }

  async append(bytes) {
    const pad = this.pos % 8 ? 8 - (this.pos % 8) : 0;  // mots alignés
    if (pad) { await this.sink.write(this.pos, new Uint8Array(pad)); this.pos += pad; }
    const offset = this.pos;
    await this.sink.write(offset, bytes);
    this.pos += bytes.length;
    return offset;
  }

  // Tuile encodée (JPEG) ; uniformKey : clé d'une tuile d'une seule couleur, écrite une fois et partagée.
  async writeTile(bytes, uniformKey = null) {
    if (uniformKey && this.shared.has(uniformKey)) return this.shared.get(uniformKey);
    const tile = { offset: await this.append(bytes), length: bytes.length };
    if (uniformKey) this.shared.set(uniformKey, tile);
    return tile;
  }

  // IFD d'entrées { tag, type, values } (triées par tag) ; valeurs de plus de 8 octets écrites avant.
  async writeIfd(entries) {
    const sorted = [...entries].sort((a, b) => a.tag - b.tag);
    const fields = [];
    for (const { tag, type, values } of sorted) {
      const data = encode(type, values);
      const count = type === TYPE.RATIONAL ? values.length / 2 : values.length;
      fields.push({ tag, type, count, data, offset: data.length > 8 ? await this.append(data) : null });
    }
    const ifd = new Uint8Array(8 + 20 * fields.length + 8);
    const view = new DataView(ifd.buffer);
    view.setBigUint64(0, BigInt(fields.length), true);
    fields.forEach(({ tag, type, count, data, offset }, i) => {
      const at = 8 + 20 * i;
      view.setUint16(at, tag, true);
      view.setUint16(at + 2, type, true);
      view.setBigUint64(at + 4, BigInt(count), true);
      if (offset == null) ifd.set(data, at + 12);
      else view.setBigUint64(at + 12, BigInt(offset), true);
    });
    return this.append(ifd);
  }

  // levels : [{ width, height, tileSize, tiles [{ offset, length }] (ligne par ligne), subsampling [h, v] }], le
  // premier en pleine résolution ; description : OME-XML.
  async finish(levels, description) {
    const ifdFor = (level, main, subIfds = []) => [
      { tag: 254, type: TYPE.LONG, values: [main ? 0 : 1] },  // NewSubfileType : réduite
      { tag: 256, type: TYPE.LONG, values: [level.width] },
      { tag: 257, type: TYPE.LONG, values: [level.height] },
      { tag: 258, type: TYPE.SHORT, values: [8, 8, 8] },
      { tag: 259, type: TYPE.SHORT, values: [7] },  // JPEG
      { tag: 262, type: TYPE.SHORT, values: [6] },  // YCbCr
      ...(main ? [{ tag: 270, type: TYPE.ASCII, values: [...new TextEncoder().encode(description), 0] }] : []),
      { tag: 277, type: TYPE.SHORT, values: [3] },
      { tag: 284, type: TYPE.SHORT, values: [1] },
      ...(main ? [{ tag: 305, type: TYPE.ASCII, values: [...new TextEncoder().encode('HyprView'), 0] }] : []),
      { tag: 322, type: TYPE.LONG, values: [level.tileSize] },
      { tag: 323, type: TYPE.LONG, values: [level.tileSize] },
      { tag: 324, type: TYPE.LONG8, values: level.tiles.map(t => t.offset) },
      { tag: 325, type: TYPE.LONG8, values: level.tiles.map(t => t.length) },
      ...(subIfds.length ? [{ tag: 330, type: TYPE.IFD8, values: subIfds }] : []),
      { tag: 530, type: TYPE.SHORT, values: level.subsampling },
      { tag: 532, type: TYPE.RATIONAL, values: [0, 1, 255, 1, 128, 1, 255, 1, 128, 1, 255, 1] },  // ReferenceBlackWhite
    ];
    const subs = [];
    for (const level of levels.slice(1)) subs.push(await this.writeIfd(ifdFor(level, false)));
    const first = await this.writeIfd(ifdFor(levels[0], true, subs));
    const header = new Uint8Array(16);
    const view = new DataView(header.buffer);
    header.set([0x49, 0x49]);  // « II » : little-endian
    view.setUint16(2, 43, true);  // BigTIFF
    view.setUint16(4, 8, true);
    view.setBigUint64(8, BigInt(first), true);
    await this.sink.write(0, header);
    await this.sink.close();
    return this.pos;
  }
}

// OME-XML d'une image RGB 8 bits entrelacée ; pixelSizeUm : taille de pixel (µm) si connue.
export function omeXml({ name, width, height, pixelSizeUm }) {
  const esc = s => String(s).replace(/[<>&"]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c]);
  const physical = pixelSizeUm
    ? ` PhysicalSizeX="${pixelSizeUm}" PhysicalSizeXUnit="µm" PhysicalSizeY="${pixelSizeUm}" PhysicalSizeYUnit="µm"` : '';
  return '<?xml version="1.0" encoding="UTF-8"?>'
    + '<OME xmlns="http://www.openmicroscopy.org/Schemas/OME/2016-06" '
    + 'xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" '
    + 'xsi:schemaLocation="http://www.openmicroscopy.org/Schemas/OME/2016-06 http://www.openmicroscopy.org/Schemas/OME/2016-06/ome.xsd">'
    + `<Image ID="Image:0" Name="${esc(name)}"><Pixels ID="Pixels:0" DimensionOrder="XYCZT" Type="uint8" SizeX="${width}" `
    + `SizeY="${height}" SizeC="3" SizeZ="1" SizeT="1" Interleaved="true"${physical}>`
    + '<Channel ID="Channel:0:0" SamplesPerPixel="3"><LightPath/></Channel>'
    + '<TiffData IFD="0" PlaneCount="1"/></Pixels></Image></OME>';
}

// Destinations : fichier choisi par l'utilisateur (File System Access, Chrome / Edge : écriture directe, sans limite
// de mémoire), ou mémoire (petites zones, autres navigateurs) puis téléchargement.
export async function fileSink(suggestedName) {
  const handle = await window.showSaveFilePicker({
    suggestedName, types: [{ description: 'OME-TIFF', accept: { 'image/tiff': ['.ome.tif'] } }],
  });
  const writable = await handle.createWritable();
  return {
    write: (position, data) => writable.write({ type: 'write', position, data }),
    close: () => writable.close(),
    abort: () => writable.abort(),
  };
}

export function memorySink() {
  let header = new Uint8Array(16);
  const parts = [];
  return {
    write: async (position, data) => {
      if (position === 0) header = data.slice();
      else parts.push(data.slice());  // écritures à la suite (positions croissantes)
    },
    close: async () => {},
    abort: async () => { parts.length = 0; },
    blob: () => new Blob([header, ...parts], { type: 'image/tiff' }),
  };
}
